/**
 * FP3-122 — Canonical date-only helpers.
 *
 * Session dates, IEP dates, evaluation due dates, and all
 * school-day dates are DATE-ONLY values stored as `YYYY-MM-DD`. The
 * common bug pattern is:
 *
 *   new Date("2026-08-10").toLocaleDateString("en-US", ...)
 *
 * — which parses the ISO string as **UTC midnight** and then
 * formats in the browser's local zone. In Arizona (UTC-7) that
 * ISO 2026-08-10 renders as "Aug 9". Same bug in any western TZ.
 *
 * Every progress chart, session-history date, and student-workspace
 * data-tab date MUST go through the helpers here so a stored value
 * of `2026-08-10` renders as `Aug 10` regardless of viewer TZ.
 */

// Parse a `YYYY-MM-DD` string into a Date whose local Y/M/D
// components equal the stored value. Does NOT touch time. Any
// other input (Date object, ISO datetime) is left alone.
export function parseDateOnly(value) {
  if (value instanceof Date) return value;
  if (typeof value !== "string") return null;
  // Accept both "2026-08-10" and "2026-08-10T..." — pull the date
  // fragment and construct via component constructor so JS uses
  // local time (never applies UTC → local shift).
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) {
    // Not a date-only string — fall back to native parsing.
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  return new Date(y, mo, d);
}

// Format a date-only value as e.g. "Aug 10". Never shifts by TZ.
export function formatMonthDay(value) {
  const d = parseDateOnly(value);
  if (!d) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Format a date-only value as e.g. "Aug 10, 2026". Never shifts.
export function formatMonthDayYear(value) {
  const d = parseDateOnly(value);
  if (!d) return "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Return the date-only string for a Date without any TZ shift.
// Preferred over `.toISOString().slice(0, 10)` which shifts west
// after midnight local time.
export function toDateOnlyString(dateOrString) {
  if (typeof dateOrString === "string") {
    // Trust an existing YYYY-MM-DD prefix.
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(dateOrString);
    if (m) return m[1];
  }
  const d = dateOrString instanceof Date ? dateOrString : new Date(dateOrString);
  if (Number.isNaN(d?.getTime?.())) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${dd}`;
}
